/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToQuery } from '../../mutators/add-meta';
import { addMetaToMutation } from '../../mutators/add-meta';
import type {
  CheckSiteMaindomainStatusParams,
  CreateAdditionalDomainRequest,
  DomainAliasResponse,
  EdgeSettingsResponse,
  Error,
  GetDomainAliasesResponse,
  ListSiteDomainsParams,
  MainDomainResponse,
  MainDomainStatusResponse,
  PatchEdgeSettingsRequest,
  PatchMainDomainPrefixRequest,
  PatchMainDomainRequest,
  PatchMainDomainResponse,
  PutMainDomainRequest,
  RecheckSiteMaindomainParams,
  SetMainDomainRequest,
  SiteID,
  StringResponse,
  StringSuccessResponse,
  SuccessResponse,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Returns extra domains pointing to the same site, beyond the primary domain. Useful for multisite setups or serving the same WordPress install on multiple domain names.
 * @summary List additional domains
 */
export const listSiteDomains = (
  id: SiteID,
  params?: ListSiteDomainsParams,
  signal?: AbortSignal
) => {
  return customFetch<GetDomainAliasesResponse>({
    url: `/v1/sites/${id}/domains`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListSiteDomainsQueryKey = (id?: SiteID, params?: ListSiteDomainsParams) => {
  return [`/v1/sites/${id}/domains`, ...(params ? [params] : [])] as const;
};

export const getListSiteDomainsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteDomainsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteDomains>>> = ({ signal }) =>
    listSiteDomains(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/domains`,
      operationId: 'app.controllers.domains_controller.list_site_domains',
      operationName: 'listSiteDomains',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listSiteDomains>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteDomainsQueryResult = NonNullable<Awaited<ReturnType<typeof listSiteDomains>>>;
export type ListSiteDomainsQueryError = Error;

export function useListSiteDomains<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | ListSiteDomainsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteDomains>>,
          TError,
          Awaited<ReturnType<typeof listSiteDomains>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteDomains<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteDomains>>,
          TError,
          Awaited<ReturnType<typeof listSiteDomains>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteDomains<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List additional domains
 */

export function useListSiteDomains<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteDomainsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListSiteDomainsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteDomainsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteDomains>>> = ({ signal }) =>
    listSiteDomains(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/domains`,
      operationId: 'app.controllers.domains_controller.list_site_domains',
      operationName: 'listSiteDomains',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listSiteDomains>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteDomainsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteDomains>>
>;
export type ListSiteDomainsSuspenseQueryError = Error;

export function useListSiteDomainsSuspense<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | ListSiteDomainsParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteDomainsSuspense<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteDomainsSuspense<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List additional domains
 */

export function useListSiteDomainsSuspense<
  TData = Awaited<ReturnType<typeof listSiteDomains>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteDomainsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteDomains>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteDomainsSuspenseQueryOptions(id, params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Connects another domain name to the same site so it serves the same WordPress installation. Common for multisite or multi-domain setups. Returns DNS and SSL setup details for the new domain.
 * @summary Add an additional domain
 */
export const createSiteDomain = (
  id: SiteID,
  createAdditionalDomainRequest: CreateAdditionalDomainRequest,
  signal?: AbortSignal
) => {
  return customFetch<DomainAliasResponse>({
    url: `/v1/sites/${id}/domains`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createAdditionalDomainRequest,
    signal,
  });
};

export const getCreateSiteDomainMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteDomain>>,
    TError,
    { id: SiteID; data: CreateAdditionalDomainRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteDomain>>,
  TError,
  { id: SiteID; data: CreateAdditionalDomainRequest },
  TContext
> => {
  const mutationKey = ['createSiteDomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteDomain>>,
    { id: SiteID; data: CreateAdditionalDomainRequest }
  > = props => {
    const { id, data } = props ?? {};

    return createSiteDomain(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/domains` },
    {
      operationId: 'app.controllers.domains_controller.create_site_domain',
      operationName: 'createSiteDomain',
    }
  );

  return customOptions;
};

export type CreateSiteDomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSiteDomain>>
>;
export type CreateSiteDomainMutationBody = CreateAdditionalDomainRequest;
export type CreateSiteDomainMutationError = Error;

/**
 * @summary Add an additional domain
 */
export const useCreateSiteDomain = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteDomain>>,
      TError,
      { id: SiteID; data: CreateAdditionalDomainRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteDomain>>,
  TError,
  { id: SiteID; data: CreateAdditionalDomainRequest },
  TContext
> => {
  const mutationOptions = getCreateSiteDomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Disconnects an additional domain from the site. The primary domain and live site are not affected. DNS records for the removed domain may need to be cleaned up at your registrar.
 * @summary Remove an additional domain
 */
export const deleteSiteDomain = (id: SiteID, domainId: number) => {
  return customFetch<StringSuccessResponse>({
    url: `/v1/sites/${id}/domains/${domainId}`,
    method: 'DELETE',
  });
};

export const getDeleteSiteDomainMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSiteDomain>>,
    TError,
    { id: SiteID; domainId: number },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSiteDomain>>,
  TError,
  { id: SiteID; domainId: number },
  TContext
> => {
  const mutationKey = ['deleteSiteDomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSiteDomain>>,
    { id: SiteID; domainId: number }
  > = props => {
    const { id, domainId } = props ?? {};

    return deleteSiteDomain(id, domainId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/domains/{domainId}` },
    {
      operationId: 'app.controllers.domains_controller.delete_site_domain',
      operationName: 'deleteSiteDomain',
    }
  );

  return customOptions;
};

export type DeleteSiteDomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSiteDomain>>
>;

export type DeleteSiteDomainMutationError = Error;

/**
 * @summary Remove an additional domain
 */
export const useDeleteSiteDomain = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSiteDomain>>,
      TError,
      { id: SiteID; domainId: number },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSiteDomain>>,
  TError,
  { id: SiteID; domainId: number },
  TContext
> => {
  const mutationOptions = getDeleteSiteDomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns security and traffic-protection settings for a specific additional domain, such as bot challenges, login protection, and crawler controls.
 * @summary Get security settings for additional domain
 */
export const findSiteDomainEdgeSettings = (id: SiteID, domainId: number, signal?: AbortSignal) => {
  return customFetch<EdgeSettingsResponse>({
    url: `/v1/sites/${id}/domains/${domainId}/edge_settings`,
    method: 'GET',
    signal,
  });
};

export const getFindSiteDomainEdgeSettingsQueryKey = (id?: SiteID, domainId?: number) => {
  return [`/v1/sites/${id}/domains/${domainId}/edge_settings`] as const;
};

export const getFindSiteDomainEdgeSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteDomainEdgeSettingsQueryKey(id, domainId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>> = ({
    signal,
  }) => findSiteDomainEdgeSettings(id, domainId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, domainId },
    {
      url: `/v1/sites/${id}/domains/${domainId}/edge_settings`,
      operationId: 'app.controllers.domains_controller.find_site_domain_edge_settings',
      operationName: 'findSiteDomainEdgeSettings',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteDomainEdgeSettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>
>;
export type FindSiteDomainEdgeSettingsQueryError = Error;

export function useFindSiteDomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
          TError,
          Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteDomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
          TError,
          Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteDomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get security settings for additional domain
 */

export function useFindSiteDomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteDomainEdgeSettingsQueryOptions(id, domainId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindSiteDomainEdgeSettingsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteDomainEdgeSettingsQueryKey(id, domainId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>> = ({
    signal,
  }) => findSiteDomainEdgeSettings(id, domainId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, domainId },
    {
      url: `/v1/sites/${id}/domains/${domainId}/edge_settings`,
      operationId: 'app.controllers.domains_controller.find_site_domain_edge_settings',
      operationName: 'findSiteDomainEdgeSettings',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteDomainEdgeSettingsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>
>;
export type FindSiteDomainEdgeSettingsSuspenseQueryError = Error;

export function useFindSiteDomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteDomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteDomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get security settings for additional domain
 */

export function useFindSiteDomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  domainId: number,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteDomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteDomainEdgeSettingsSuspenseQueryOptions(id, domainId, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Enables or adjusts protection features for a specific additional domain, independent of settings on the primary domain.
 * @summary Update security settings for additional domain
 */
export const modifySiteDomainEdgeSettings = (
  id: SiteID,
  domainId: number,
  patchEdgeSettingsRequest: PatchEdgeSettingsRequest
) => {
  return customFetch<EdgeSettingsResponse>({
    url: `/v1/sites/${id}/domains/${domainId}/edge_settings`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: patchEdgeSettingsRequest,
  });
};

export const getModifySiteDomainEdgeSettingsMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifySiteDomainEdgeSettings>>,
    TError,
    { id: SiteID; domainId: number; data: PatchEdgeSettingsRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifySiteDomainEdgeSettings>>,
  TError,
  { id: SiteID; domainId: number; data: PatchEdgeSettingsRequest },
  TContext
> => {
  const mutationKey = ['modifySiteDomainEdgeSettings'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifySiteDomainEdgeSettings>>,
    { id: SiteID; domainId: number; data: PatchEdgeSettingsRequest }
  > = props => {
    const { id, domainId, data } = props ?? {};

    return modifySiteDomainEdgeSettings(id, domainId, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/domains/{domainId}/edge_settings` },
    {
      operationId: 'app.controllers.domains_controller.modify_site_domain_edge_settings',
      operationName: 'modifySiteDomainEdgeSettings',
    }
  );

  return customOptions;
};

export type ModifySiteDomainEdgeSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof modifySiteDomainEdgeSettings>>
>;
export type ModifySiteDomainEdgeSettingsMutationBody = PatchEdgeSettingsRequest;
export type ModifySiteDomainEdgeSettingsMutationError = Error;

/**
 * @summary Update security settings for additional domain
 */
export const useModifySiteDomainEdgeSettings = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifySiteDomainEdgeSettings>>,
      TError,
      { id: SiteID; domainId: number; data: PatchEdgeSettingsRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifySiteDomainEdgeSettings>>,
  TError,
  { id: SiteID; domainId: number; data: PatchEdgeSettingsRequest },
  TContext
> => {
  const mutationOptions = getModifySiteDomainEdgeSettingsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns information about the site's primary custom domain, including SSL status, validation records needed to go live, and whether the site is still on its preview URL.
 * @summary Get primary domain details
 */
export const findSiteMaindomain = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<MainDomainResponse>({
    url: `/v1/sites/${id}/maindomain`,
    method: 'GET',
    signal,
  });
};

export const getFindSiteMaindomainQueryKey = (id?: SiteID) => {
  return [`/v1/sites/${id}/maindomain`] as const;
};

export const getFindSiteMaindomainQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteMaindomainQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteMaindomain>>> = ({ signal }) =>
    findSiteMaindomain(id, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id },
    {
      url: `/v1/sites/${id}/maindomain`,
      operationId: 'app.controllers.maindomain_controller.find_site_maindomain',
      operationName: 'findSiteMaindomain',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findSiteMaindomain>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteMaindomainQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteMaindomain>>
>;
export type FindSiteMaindomainQueryError = Error;

export function useFindSiteMaindomain<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteMaindomain>>,
          TError,
          Awaited<ReturnType<typeof findSiteMaindomain>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomain<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteMaindomain>>,
          TError,
          Awaited<ReturnType<typeof findSiteMaindomain>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomain<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get primary domain details
 */

export function useFindSiteMaindomain<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteMaindomainQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindSiteMaindomainSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteMaindomainQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteMaindomain>>> = ({ signal }) =>
    findSiteMaindomain(id, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id },
    {
      url: `/v1/sites/${id}/maindomain`,
      operationId: 'app.controllers.maindomain_controller.find_site_maindomain',
      operationName: 'findSiteMaindomain',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findSiteMaindomain>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteMaindomainSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteMaindomain>>
>;
export type FindSiteMaindomainSuspenseQueryError = Error;

export function useFindSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get primary domain details
 */

export function useFindSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteMaindomainSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Assigns a custom domain to a site that is still on its preview URL. Returns the DNS records you need to add. Not available on staging sites. After the first domain is set, use PUT to change it.
 * @summary Connect your domain (first time)
 */
export const createSiteMaindomain = (
  id: SiteID,
  setMainDomainRequest: SetMainDomainRequest,
  signal?: AbortSignal
) => {
  return customFetch<MainDomainResponse>({
    url: `/v1/sites/${id}/maindomain`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: setMainDomainRequest,
    signal,
  });
};

export const getCreateSiteMaindomainMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteMaindomain>>,
    TError,
    { id: SiteID; data: SetMainDomainRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteMaindomain>>,
  TError,
  { id: SiteID; data: SetMainDomainRequest },
  TContext
> => {
  const mutationKey = ['createSiteMaindomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteMaindomain>>,
    { id: SiteID; data: SetMainDomainRequest }
  > = props => {
    const { id, data } = props ?? {};

    return createSiteMaindomain(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/maindomain` },
    {
      operationId: 'app.controllers.maindomain_controller.create_site_maindomain',
      operationName: 'createSiteMaindomain',
    }
  );

  return customOptions;
};

export type CreateSiteMaindomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSiteMaindomain>>
>;
export type CreateSiteMaindomainMutationBody = SetMainDomainRequest;
export type CreateSiteMaindomainMutationError = Error;

/**
 * @summary Connect your domain (first time)
 */
export const useCreateSiteMaindomain = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteMaindomain>>,
      TError,
      { id: SiteID; data: SetMainDomainRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteMaindomain>>,
  TError,
  { id: SiteID; data: SetMainDomainRequest },
  TContext
> => {
  const mutationOptions = getCreateSiteMaindomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Changes how your domain is verified (DNS records or HTTP file) or which certificate provider issues your SSL certificate. You can also dismiss go-live setup prompts once your domain is connected.
 * @summary Update domain verification or SSL settings
 */
export const modifySiteMaindomain = (
  id: SiteID,
  patchMainDomainRequest: PatchMainDomainRequest
) => {
  return customFetch<PatchMainDomainResponse>({
    url: `/v1/sites/${id}/maindomain`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: patchMainDomainRequest,
  });
};

export const getModifySiteMaindomainMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifySiteMaindomain>>,
    TError,
    { id: SiteID; data: PatchMainDomainRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifySiteMaindomain>>,
  TError,
  { id: SiteID; data: PatchMainDomainRequest },
  TContext
> => {
  const mutationKey = ['modifySiteMaindomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifySiteMaindomain>>,
    { id: SiteID; data: PatchMainDomainRequest }
  > = props => {
    const { id, data } = props ?? {};

    return modifySiteMaindomain(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/maindomain` },
    {
      operationId: 'app.controllers.maindomain_controller.modify_site_maindomain',
      operationName: 'modifySiteMaindomain',
    }
  );

  return customOptions;
};

export type ModifySiteMaindomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof modifySiteMaindomain>>
>;
export type ModifySiteMaindomainMutationBody = PatchMainDomainRequest;
export type ModifySiteMaindomainMutationError = Error;

/**
 * @summary Update domain verification or SSL settings
 */
export const useModifySiteMaindomain = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifySiteMaindomain>>,
      TError,
      { id: SiteID; data: PatchMainDomainRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifySiteMaindomain>>,
  TError,
  { id: SiteID; data: PatchMainDomainRequest },
  TContext
> => {
  const mutationOptions = getModifySiteMaindomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Replaces the site's current primary domain with a new one. Can also switch WordPress between the live domain and preview URL, promote an additional domain to primary, or revert to preview-only mode.
 * @summary Change the site's primary domain
 */
export const updateSiteMaindomain = (id: SiteID, putMainDomainRequest: PutMainDomainRequest) => {
  return customFetch<MainDomainResponse>({
    url: `/v1/sites/${id}/maindomain`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: putMainDomainRequest,
  });
};

export const getUpdateSiteMaindomainMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSiteMaindomain>>,
    TError,
    { id: SiteID; data: PutMainDomainRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateSiteMaindomain>>,
  TError,
  { id: SiteID; data: PutMainDomainRequest },
  TContext
> => {
  const mutationKey = ['updateSiteMaindomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateSiteMaindomain>>,
    { id: SiteID; data: PutMainDomainRequest }
  > = props => {
    const { id, data } = props ?? {};

    return updateSiteMaindomain(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/maindomain` },
    {
      operationId: 'app.controllers.maindomain_controller.update_site_maindomain',
      operationName: 'updateSiteMaindomain',
    }
  );

  return customOptions;
};

export type UpdateSiteMaindomainMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateSiteMaindomain>>
>;
export type UpdateSiteMaindomainMutationBody = PutMainDomainRequest;
export type UpdateSiteMaindomainMutationError = Error;

/**
 * @summary Change the site's primary domain
 */
export const useUpdateSiteMaindomain = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateSiteMaindomain>>,
      TError,
      { id: SiteID; data: PutMainDomainRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateSiteMaindomain>>,
  TError,
  { id: SiteID; data: PutMainDomainRequest },
  TContext
> => {
  const mutationOptions = getUpdateSiteMaindomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns security and traffic-protection settings for the primary domain, such as bot challenges, login protection, WooCommerce safeguards, and AI crawler controls. Requires a connected live domain.
 * @summary Get security settings for primary domain
 */
export const findSiteMaindomainEdgeSettings = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<EdgeSettingsResponse>({
    url: `/v1/sites/${id}/maindomain/edge_settings`,
    method: 'GET',
    signal,
  });
};

export const getFindSiteMaindomainEdgeSettingsQueryKey = (id?: SiteID) => {
  return [`/v1/sites/${id}/maindomain/edge_settings`] as const;
};

export const getFindSiteMaindomainEdgeSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteMaindomainEdgeSettingsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>> = ({
    signal,
  }) => findSiteMaindomainEdgeSettings(id, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id },
    {
      url: `/v1/sites/${id}/maindomain/edge_settings`,
      operationId: 'app.controllers.maindomain_controller.find_site_maindomain_edge_settings',
      operationName: 'findSiteMaindomainEdgeSettings',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteMaindomainEdgeSettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>
>;
export type FindSiteMaindomainEdgeSettingsQueryError = Error;

export function useFindSiteMaindomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
          TError,
          Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
          TError,
          Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get security settings for primary domain
 */

export function useFindSiteMaindomainEdgeSettings<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteMaindomainEdgeSettingsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindSiteMaindomainEdgeSettingsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
        TError,
        TData
      >
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteMaindomainEdgeSettingsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>> = ({
    signal,
  }) => findSiteMaindomainEdgeSettings(id, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id },
    {
      url: `/v1/sites/${id}/maindomain/edge_settings`,
      operationId: 'app.controllers.maindomain_controller.find_site_maindomain_edge_settings',
      operationName: 'findSiteMaindomainEdgeSettings',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteMaindomainEdgeSettingsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>
>;
export type FindSiteMaindomainEdgeSettingsSuspenseQueryError = Error;

export function useFindSiteMaindomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteMaindomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get security settings for primary domain
 */

export function useFindSiteMaindomainEdgeSettingsSuspense<
  TData = Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
  TError = Error,
>(
  id: SiteID,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof findSiteMaindomainEdgeSettings>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteMaindomainEdgeSettingsSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Enables or adjusts protection features for the primary domain—for example, challenge pages under attack, WordPress login protection, WooCommerce login safeguards, and blocking suspicious traffic.
 * @summary Update security settings for primary domain
 */
export const modifySiteMaindomainEdgeSettings = (
  id: SiteID,
  patchEdgeSettingsRequest: PatchEdgeSettingsRequest
) => {
  return customFetch<EdgeSettingsResponse>({
    url: `/v1/sites/${id}/maindomain/edge_settings`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: patchEdgeSettingsRequest,
  });
};

export const getModifySiteMaindomainEdgeSettingsMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifySiteMaindomainEdgeSettings>>,
    TError,
    { id: SiteID; data: PatchEdgeSettingsRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifySiteMaindomainEdgeSettings>>,
  TError,
  { id: SiteID; data: PatchEdgeSettingsRequest },
  TContext
> => {
  const mutationKey = ['modifySiteMaindomainEdgeSettings'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifySiteMaindomainEdgeSettings>>,
    { id: SiteID; data: PatchEdgeSettingsRequest }
  > = props => {
    const { id, data } = props ?? {};

    return modifySiteMaindomainEdgeSettings(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/maindomain/edge_settings` },
    {
      operationId: 'app.controllers.maindomain_controller.modify_site_maindomain_edge_settings',
      operationName: 'modifySiteMaindomainEdgeSettings',
    }
  );

  return customOptions;
};

export type ModifySiteMaindomainEdgeSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof modifySiteMaindomainEdgeSettings>>
>;
export type ModifySiteMaindomainEdgeSettingsMutationBody = PatchEdgeSettingsRequest;
export type ModifySiteMaindomainEdgeSettingsMutationError = Error;

/**
 * @summary Update security settings for primary domain
 */
export const useModifySiteMaindomainEdgeSettings = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifySiteMaindomainEdgeSettings>>,
      TError,
      { id: SiteID; data: PatchEdgeSettingsRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifySiteMaindomainEdgeSettings>>,
  TError,
  { id: SiteID; data: PatchEdgeSettingsRequest },
  TContext
> => {
  const mutationOptions = getModifySiteMaindomainEdgeSettingsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Changes whether your primary domain uses www (for example, www.example.com) or the bare domain (for example, example.com). The alternate version is configured automatically.
 * @summary Switch between www and non-www
 */
export const switchSiteMaindomainPrefix = (
  id: SiteID,
  patchMainDomainPrefixRequest: PatchMainDomainPrefixRequest
) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/${id}/maindomain/prefix`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: patchMainDomainPrefixRequest,
  });
};

export const getSwitchSiteMaindomainPrefixMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof switchSiteMaindomainPrefix>>,
    TError,
    { id: SiteID; data: PatchMainDomainPrefixRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof switchSiteMaindomainPrefix>>,
  TError,
  { id: SiteID; data: PatchMainDomainPrefixRequest },
  TContext
> => {
  const mutationKey = ['switchSiteMaindomainPrefix'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof switchSiteMaindomainPrefix>>,
    { id: SiteID; data: PatchMainDomainPrefixRequest }
  > = props => {
    const { id, data } = props ?? {};

    return switchSiteMaindomainPrefix(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/maindomain/prefix` },
    {
      operationId: 'app.controllers.maindomain_controller.switch_site_maindomain_prefix',
      operationName: 'switchSiteMaindomainPrefix',
    }
  );

  return customOptions;
};

export type SwitchSiteMaindomainPrefixMutationResult = NonNullable<
  Awaited<ReturnType<typeof switchSiteMaindomainPrefix>>
>;
export type SwitchSiteMaindomainPrefixMutationBody = PatchMainDomainPrefixRequest;
export type SwitchSiteMaindomainPrefixMutationError = Error;

/**
 * @summary Switch between www and non-www
 */
export const useSwitchSiteMaindomainPrefix = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof switchSiteMaindomainPrefix>>,
      TError,
      { id: SiteID; data: PatchMainDomainPrefixRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof switchSiteMaindomainPrefix>>,
  TError,
  { id: SiteID; data: PatchMainDomainPrefixRequest },
  TContext
> => {
  const mutationOptions = getSwitchSiteMaindomainPrefixMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Triggers an immediate re-check of DNS records and SSL certificate status for the primary domain, alternate domain, and any additional domains. Use after updating DNS or when validation seems stuck.
 * @summary Recheck domain and SSL status
 */
export const recheckSiteMaindomain = (
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  signal?: AbortSignal
) => {
  return customFetch<StringResponse>({
    url: `/v1/sites/${id}/maindomain/recheck`,
    method: 'GET',
    params,
    signal,
  });
};

export const getRecheckSiteMaindomainQueryKey = (
  id?: SiteID,
  params?: RecheckSiteMaindomainParams
) => {
  return [`/v1/sites/${id}/maindomain/recheck`, ...(params ? [params] : [])] as const;
};

export const getRecheckSiteMaindomainQueryOptions = <
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRecheckSiteMaindomainQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof recheckSiteMaindomain>>> = ({ signal }) =>
    recheckSiteMaindomain(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/maindomain/recheck`,
      operationId: 'app.controllers.maindomain_controller.recheck_site_maindomain',
      operationName: 'recheckSiteMaindomain',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof recheckSiteMaindomain>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RecheckSiteMaindomainQueryResult = NonNullable<
  Awaited<ReturnType<typeof recheckSiteMaindomain>>
>;
export type RecheckSiteMaindomainQueryError = Error;

export function useRecheckSiteMaindomain<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | RecheckSiteMaindomainParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof recheckSiteMaindomain>>,
          TError,
          Awaited<ReturnType<typeof recheckSiteMaindomain>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRecheckSiteMaindomain<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof recheckSiteMaindomain>>,
          TError,
          Awaited<ReturnType<typeof recheckSiteMaindomain>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRecheckSiteMaindomain<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Recheck domain and SSL status
 */

export function useRecheckSiteMaindomain<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRecheckSiteMaindomainQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getRecheckSiteMaindomainSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRecheckSiteMaindomainQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof recheckSiteMaindomain>>> = ({ signal }) =>
    recheckSiteMaindomain(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/maindomain/recheck`,
      operationId: 'app.controllers.maindomain_controller.recheck_site_maindomain',
      operationName: 'recheckSiteMaindomain',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof recheckSiteMaindomain>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RecheckSiteMaindomainSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof recheckSiteMaindomain>>
>;
export type RecheckSiteMaindomainSuspenseQueryError = Error;

export function useRecheckSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | RecheckSiteMaindomainParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRecheckSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRecheckSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Recheck domain and SSL status
 */

export function useRecheckSiteMaindomainSuspense<
  TData = Awaited<ReturnType<typeof recheckSiteMaindomain>>,
  TError = Error,
>(
  id: SiteID,
  params?: RecheckSiteMaindomainParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof recheckSiteMaindomain>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRecheckSiteMaindomainSuspenseQueryOptions(id, params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns a detailed health report for every domain on the site: primary, www or alternate, and additional domains. Includes SSL status, expected versus found DNS records, validation errors, and records that need to be removed.
 * @summary Check all domain statuses
 */
export const checkSiteMaindomainStatus = (
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  signal?: AbortSignal
) => {
  return customFetch<MainDomainStatusResponse>({
    url: `/v1/sites/${id}/maindomain/status`,
    method: 'GET',
    params,
    signal,
  });
};

export const getCheckSiteMaindomainStatusQueryKey = (
  id?: SiteID,
  params?: CheckSiteMaindomainStatusParams
) => {
  return [`/v1/sites/${id}/maindomain/status`, ...(params ? [params] : [])] as const;
};

export const getCheckSiteMaindomainStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCheckSiteMaindomainStatusQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>> = ({
    signal,
  }) => checkSiteMaindomainStatus(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/maindomain/status`,
      operationId: 'app.controllers.maindomain_controller.check_site_maindomain_status',
      operationName: 'checkSiteMaindomainStatus',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CheckSiteMaindomainStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof checkSiteMaindomainStatus>>
>;
export type CheckSiteMaindomainStatusQueryError = Error;

export function useCheckSiteMaindomainStatus<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | CheckSiteMaindomainStatusParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
          TError,
          Awaited<ReturnType<typeof checkSiteMaindomainStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCheckSiteMaindomainStatus<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
          TError,
          Awaited<ReturnType<typeof checkSiteMaindomainStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCheckSiteMaindomainStatus<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Check all domain statuses
 */

export function useCheckSiteMaindomainStatus<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCheckSiteMaindomainStatusQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getCheckSiteMaindomainStatusSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCheckSiteMaindomainStatusQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>> = ({
    signal,
  }) => checkSiteMaindomainStatus(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/maindomain/status`,
      operationId: 'app.controllers.maindomain_controller.check_site_maindomain_status',
      operationName: 'checkSiteMaindomainStatus',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CheckSiteMaindomainStatusSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof checkSiteMaindomainStatus>>
>;
export type CheckSiteMaindomainStatusSuspenseQueryError = Error;

export function useCheckSiteMaindomainStatusSuspense<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | CheckSiteMaindomainStatusParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCheckSiteMaindomainStatusSuspense<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCheckSiteMaindomainStatusSuspense<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Check all domain statuses
 */

export function useCheckSiteMaindomainStatusSuspense<
  TData = Awaited<ReturnType<typeof checkSiteMaindomainStatus>>,
  TError = Error,
>(
  id: SiteID,
  params?: CheckSiteMaindomainStatusParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof checkSiteMaindomainStatus>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCheckSiteMaindomainStatusSuspenseQueryOptions(id, params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
